import {
	IconChevronLeft,
	IconChevronRight,
	IconChevronsLeft,
	IconChevronsRight,
} from "@tabler/icons-react";
import {
	type ColumnDef,
	type ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFacetedRowModel,
	getFacetedUniqueValues,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type Row,
	type SortingState,
	type TableOptions,
	useReactTable,
} from "@tanstack/react-table";
import { ScanSearchIcon } from "lucide-react";
import * as React from "react";
import z from "zod";
import { Button } from "@/components/ui/button";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/use-debounce-value";

export const DataTableSearchSchema = z.object({
	q: z.string().optional(),
	page: z.coerce.number().int().min(1).optional(),
	pageSize: z.coerce.number().int().min(1).optional(),
	sort: z.string().optional(),
	filters: z.string().optional(),
});

export type DataTableSearchParams = z.infer<typeof DataTableSearchSchema>;

export interface DataTableConfig {
	defaultPageSize?: number;
	pageSizeOptions?: Array<number>;
	debounceMs?: number;
	autoResetPageIndex?: boolean;
}

export interface DataTableProps<TData> {
	columns: Array<ColumnDef<TData>>;
	data: Array<TData>;
	toolbar?: React.ReactNode;
	config?: DataTableConfig;
	searchParams?: DataTableSearchParams;
	onNavigate?: (params: DataTableSearchParams) => void;
	getRowId?: TableOptions<TData>["getRowId"];
	enableRowSelection?: boolean;
	isFetching?: boolean;
	handleReload?: () => void;
	renderRow?: (row: Row<TData>) => React.ReactNode;
}

function parseFilters(filtersString?: string): ColumnFiltersState {
	if (!filtersString) return [];

	try {
		return JSON.parse(filtersString);
	} catch {
		return [];
	}
}

function parseSort(sortString?: string): SortingState {
	if (!sortString) return [];

	return sortString.split(",").map((s) => {
		const [id, direction] = s.split(":");
		return { id, desc: direction === "desc" };
	});
}

export function DataTableGeneric<TData>({
	columns,
	data: initialData,
	isFetching = false,
	handleReload,
	toolbar,
	config = {},
	searchParams = {},
	onNavigate,
	getRowId,
	enableRowSelection = true,
	renderRow,
}: DataTableProps<TData>) {
	const {
		defaultPageSize = 25,
		pageSizeOptions = [25, 50, 100, 500],
		debounceMs = 250,
		autoResetPageIndex = false,
	} = config;

	const [data, setData] = React.useState(() => initialData);
	const [rowSelection, setRowSelection] = React.useState({});

	const [globalFilter, setGlobalFilter] = React.useState(searchParams.q ?? "");
	const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
		parseFilters(searchParams.filters),
	);
	const [sorting, setSorting] = React.useState<SortingState>(
		parseSort(searchParams.sort),
	);
	const [pagination, setPagination] = React.useState({
		pageIndex: (searchParams.page ?? 1) - 1,
		pageSize: searchParams.pageSize ?? defaultPageSize,
	});

	React.useEffect(() => {
		setData(initialData);
	}, [initialData]);

	React.useEffect(() => {
		setGlobalFilter(searchParams.q ?? "");
	}, [searchParams.q]);

	const debouncedGlobal = useDebouncedValue(globalFilter, debounceMs);

	React.useEffect(() => {
		if (onNavigate) {
			onNavigate({
				q: debouncedGlobal || undefined,
				page: pagination.pageIndex + 1,
				pageSize:
					pagination.pageSize !== defaultPageSize
						? pagination.pageSize
						: undefined,
				sort: sorting.length
					? sorting.map((s) => `${s.id}:${s.desc ? "desc" : "asc"}`).join(",")
					: undefined,
				filters: columnFilters.length
					? JSON.stringify(columnFilters)
					: undefined,
			});
		}
	}, [
		debouncedGlobal,
		pagination.pageIndex,
		pagination.pageSize,
		sorting,
		columnFilters,
		onNavigate,
		defaultPageSize,
	]);

	const table = useReactTable({
		data,
		columns,
		state: {
			columnFilters,
			globalFilter,
			pagination,
			rowSelection,
			sorting,
		},
		autoResetPageIndex,
		enableRowSelection,
		getRowId,
		getCoreRowModel: getCoreRowModel(),
		getFacetedRowModel: getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		onColumnFiltersChange: (updater) => {
			setPagination((old) => ({ ...old, pageIndex: 0 }));
			setColumnFilters(updater);
		},
		onGlobalFilterChange: (updater) => {
			setPagination((old) => ({ ...old, pageIndex: 0 }));
			setGlobalFilter(updater);
		},
		onPaginationChange: setPagination,
		onRowSelectionChange: setRowSelection,
		onSortingChange: setSorting,
	});

	return (
		<div className="w-full flex flex-col gap-6">
			{toolbar && (
				<div className="flex items-center justify-between px-4 lg:px-6 gap-4">
					{toolbar}
				</div>
			)}
			<div className="relative flex flex-col gap-4 overflow-auto px-4 lg:px-6">
				<div className="flex items-center justify-between px-4">
					<div className="text-muted-foreground hidden flex-1 text-sm lg:flex">
						{table.getFilteredSelectedRowModel().rows.length} of{" "}
						{table.getFilteredRowModel().rows.length} row(s) selected.
					</div>
					<div className="flex w-full items-center gap-8 lg:w-fit">
						<div className="hidden items-center gap-2 lg:flex">
							<Label htmlFor="rows-per-page" className="text-sm font-medium">
								Rows per page
							</Label>
							<Select
								value={`${table.getState().pagination.pageSize}`}
								onValueChange={(value) => {
									table.setPageSize(Number(value));
								}}
							>
								<SelectTrigger size="sm" className="w-20" id="rows-per-page">
									<SelectValue
										placeholder={table.getState().pagination.pageSize}
									/>
								</SelectTrigger>
								<SelectContent side="top">
									{pageSizeOptions.map((pageSize) => (
										<SelectItem key={pageSize} value={`${pageSize}`}>
											{pageSize}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex w-fit items-center justify-center text-sm font-medium">
							Page {table.getState().pagination.pageIndex + 1} of{" "}
							{table.getPageCount()}
						</div>
						<div className="ml-auto flex items-center gap-2 lg:ml-0">
							<Button
								variant="outline"
								className="hidden h-8 w-8 p-0 lg:flex"
								onClick={() => table.setPageIndex(0)}
								disabled={!table.getCanPreviousPage()}
							>
								<span className="sr-only">Go to first page</span>
								<IconChevronsLeft />
							</Button>
							<Button
								variant="outline"
								className="size-8"
								size="icon"
								onClick={() => table.previousPage()}
								disabled={!table.getCanPreviousPage()}
							>
								<span className="sr-only">Go to previous page</span>
								<IconChevronLeft />
							</Button>
							<Button
								variant="outline"
								className="size-8"
								size="icon"
								onClick={() => table.nextPage()}
								disabled={!table.getCanNextPage()}
							>
								<span className="sr-only">Go to next page</span>
								<IconChevronRight />
							</Button>
							<Button
								variant="outline"
								className="hidden size-8 lg:flex"
								size="icon"
								onClick={() => table.setPageIndex(table.getPageCount() - 1)}
								disabled={!table.getCanNextPage()}
							>
								<span className="sr-only">Go to last page</span>
								<IconChevronsRight />
							</Button>
						</div>
					</div>
				</div>
				<div className="overflow-hidden rounded-lg border">
					<Table>
						<TableHeader className="bg-muted sticky top-0 z-10">
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										return (
											<TableHead key={header.id} colSpan={header.colSpan}>
												{header.isPlaceholder
													? null
													: flexRender(
															header.column.columnDef.header,
															header.getContext(),
														)}
											</TableHead>
										);
									})}
								</TableRow>
							))}
						</TableHeader>
						<TableBody className="**:data-[slot=table-cell]:first:w-8">
							{table.getRowModel().rows?.length ? (
								table.getRowModel().rows.map((row) =>
									renderRow ? (
										renderRow(row)
									) : (
										<TableRow
											key={row.id}
											data-state={row.getIsSelected() && "selected"}
											className="relative z-0"
										>
											{row.getVisibleCells().map((cell) => (
												<TableCell key={cell.id}>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext(),
													)}
												</TableCell>
											))}
										</TableRow>
									),
								)
							) : isFetching ? (
								<TableRow>
									<TableCell colSpan={columns.length}>
										<Empty className="w-full">
											<EmptyHeader>
												<EmptyMedia variant="icon">
													<Spinner />
												</EmptyMedia>
												<EmptyTitle>Loading resource</EmptyTitle>
												<EmptyDescription>
													Please wait while we load the requested resource.
												</EmptyDescription>
											</EmptyHeader>
										</Empty>
									</TableCell>
								</TableRow>
							) : (
								<TableRow>
									<TableCell colSpan={columns.length}>
										<Empty className="w-full">
											<EmptyHeader>
												<EmptyMedia variant="icon">
													<ScanSearchIcon />
												</EmptyMedia>
												<EmptyTitle>No results</EmptyTitle>
												<EmptyDescription>
													There are no results matching your search.
												</EmptyDescription>
											</EmptyHeader>
											{handleReload ? (
												<EmptyContent>
													<Button
														variant="outline"
														size="sm"
														onClick={() => handleReload()}
													>
														Try again
													</Button>
												</EmptyContent>
											) : null}
										</Empty>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
				<div className="flex items-center justify-between px-4">
					<div className="text-muted-foreground hidden flex-1 text-sm lg:flex">
						{table.getFilteredSelectedRowModel().rows.length} of{" "}
						{table.getFilteredRowModel().rows.length} row(s) selected.
					</div>
					<div className="flex w-full items-center gap-8 lg:w-fit">
						<div className="hidden items-center gap-2 lg:flex">
							<Label htmlFor="rows-per-page" className="text-sm font-medium">
								Rows per page
							</Label>
							<Select
								value={`${table.getState().pagination.pageSize}`}
								onValueChange={(value) => {
									table.setPageSize(Number(value));
								}}
							>
								<SelectTrigger size="sm" className="w-20" id="rows-per-page">
									<SelectValue
										placeholder={table.getState().pagination.pageSize}
									/>
								</SelectTrigger>
								<SelectContent side="top">
									{pageSizeOptions.map((pageSize) => (
										<SelectItem key={pageSize} value={`${pageSize}`}>
											{pageSize}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex w-fit items-center justify-center text-sm font-medium">
							Page {table.getState().pagination.pageIndex + 1} of{" "}
							{table.getPageCount()}
						</div>
						<div className="ml-auto flex items-center gap-2 lg:ml-0">
							<Button
								variant="outline"
								className="hidden h-8 w-8 p-0 lg:flex"
								onClick={() => table.setPageIndex(0)}
								disabled={!table.getCanPreviousPage()}
							>
								<span className="sr-only">Go to first page</span>
								<IconChevronsLeft />
							</Button>
							<Button
								variant="outline"
								className="size-8"
								size="icon"
								onClick={() => table.previousPage()}
								disabled={!table.getCanPreviousPage()}
							>
								<span className="sr-only">Go to previous page</span>
								<IconChevronLeft />
							</Button>
							<Button
								variant="outline"
								className="size-8"
								size="icon"
								onClick={() => table.nextPage()}
								disabled={!table.getCanNextPage()}
							>
								<span className="sr-only">Go to next page</span>
								<IconChevronRight />
							</Button>
							<Button
								variant="outline"
								className="hidden size-8 lg:flex"
								size="icon"
								onClick={() => table.setPageIndex(table.getPageCount() - 1)}
								disabled={!table.getCanNextPage()}
							>
								<span className="sr-only">Go to last page</span>
								<IconChevronsRight />
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export { parseFilters, parseSort };
